import { createStore } from 'zustand';
import { CmsProductForSale } from 'utility/node/sanity/sanity';
import { isProductRecommended } from 'utility/buy-insurances';
import { randomString } from 'utility/string';

export type ProductState = {
  selectedProductIds: string[];
  productsToBuy: BuyInsuranceProduct[];
};

export interface ProductActions extends ProductState {
  removeProductId: (id: string, amount: number, isRecommended: boolean) => void;
  clearProducts: () => void;
  addProductToBuy: (
    product: ProductForSales | CmsProductForSale,
    newProductId: string,
    amount: number,
    isRecommended: boolean,
  ) => void;
  getCountByProductId: (id: string) => number;
}

export type ProductStore = ProductState & ProductActions;

export const createProductsStore = (initProps?: ProductState) => {
  const defaultProps: ProductState = {
    selectedProductIds: [],
    productsToBuy: [],
  };
  return createStore<ProductStore>()((set, get) => ({
    ...defaultProps,
    ...initProps,
    getCountByProductId: (id: string) =>
      get().productsToBuy.filter((product) => product.productId === id).length,
    removeProductId: (id, amount, isRecommended) => {
      set((state) => {
        const existingQuantity =
          state.productsToBuy.filter((product) => product.productId === id)
            .length || 0;

        // If amount is 0, remove all products with id
        let quantityToRemove = amount
          ? existingQuantity - amount
          : existingQuantity;

        return {
          selectedProductIds: state.selectedProductIds.filter((product) => {
            // If amount is 0 and id matches, remove id string from array
            // Or if product is recommended and id matches, remove id string from array since it can only be 1 right now
            return !(
              (amount === 0 && product === id) ||
              (product === id && isRecommended)
            );
          }),
          productsToBuy: state.productsToBuy.filter((product) => {
            if (isProductRecommended(product) && product.productId === id) {
              return false;
            }
            if (product.productId === id && quantityToRemove > 0) {
              quantityToRemove--;
              return false;
            }

            return true;
          }),
        };
      });
    },

    clearProducts: () => set(defaultProps),

    addProductToBuy: (product, newProductId, amount, isRecommended) => {
      set((state) => {
        const existingQuantity =
          state.productsToBuy.filter(
            (product) => product.productId === newProductId,
          ).length || 0;
        let quantityToAdd = amount - existingQuantity;

        const productsToAdd: BuyInsuranceProduct[] = Array.from({
          length: quantityToAdd,
        }).map(() => ({
          cmsProductForSale: product,
          buyInsuranceId: randomString(),
          productId: newProductId,
          productSelected: true,
          ...(isRecommended && {
            shortDescription: `${product?.description}`,
            objectFields: (product as ProductForSales)?.objectFields,
            recommendation: {
              recommended: true,
              modelId: (product as ProductForSales)?.modelId,
              requestID: (product as ProductForSales)?.requestId,
              feedbackProvided: false,
            },
          }),
        }));

        return {
          selectedProductIds: [...state.selectedProductIds, newProductId],
          productsToBuy: [...state.productsToBuy, ...productsToAdd],
        };
      });
    },
  }));
};

export interface BuyInsuranceProduct {
  productId?: string;
  buyInsuranceId: string;
  productSelected: boolean;
  cmsProductForSale: CmsProductForSale;
  shortDescription?: string;
  objectFields?: { [key: string]: string };
  recommendation?: {
    recommended: boolean;
    modelId: string;
    requestID: string;
    feedbackProvided: boolean;
  };
}

export interface ProductForSales extends CmsProductForSale {
  modelId: string;
  requestId: string;
  objectFields?: { [key: string]: string };
  shortDescription?: string;
}
